import { Award, Users, BookOpen, Heart } from 'lucide-react';

const CAVEMAN_LAPTOP = "/images/caveman-laptop.png";

const highlights = [
  {
    icon: Award,
    title: '35+ Years Experience',
    desc: 'Decades of expertise in appraisals, lending, and customer service.',
  },
  {
    icon: Users,
    title: 'Knowledgeable Team',
    desc: 'Our team members will assist you in finding the true value of your items.',
  },
  {
    icon: BookOpen,
    title: 'Extensive Library',
    desc: 'We have an entire reference library dedicated to accurate item valuations.',
  },
  {
    icon: Heart,
    title: 'Community First',
    desc: 'We are your neighbors. We care about helping people in our community.',
  },
];

export default function AboutSection() {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative py-20 md:py-28 bg-stone-light noise-overlay overflow-hidden"
      aria-label="About Cave City Pawn Shop"
    >
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 md:gap-14 items-center">
          {/* Left content */}
          <div className="lg:col-span-3">
            <p className="text-sm font-body font-semibold text-magma-red uppercase tracking-widest mb-3">Est. 2020</p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-heading text-dirt-brown uppercase mb-5 chisel-heading">
              About <span className="text-magma-red">Cave City</span> Pawn
            </h2>

            <p className="text-base md:text-lg text-foreground font-body leading-relaxed mb-4">
              Established in 2020, <strong>Cave City Pawn Shop &amp; Trading Company</strong> brings 
              over 35 years of combined industry experience to serve our community. We are not your 
              average pawn shop - we are a full-service trading company offering fair deals on everything 
              from firearms and electronics to fine jewelry and musical instruments.
            </p>
            <p className="text-base md:text-lg text-foreground font-body leading-relaxed mb-6">
              Our knowledgeable team members are passionate about what they do. Whether you are curious 
              about what grandma's old ring is worth, need quick cash to get through a tough week, or 
              you are hunting for the deal of a lifetime - Cave City Pawn is your destination. We have 
              an entire reference library dedicated to helping us accurately appraise your items.
            </p>
            <p className="text-base md:text-lg text-foreground font-body leading-relaxed mb-8">
              If you have been given items and you are just curious about what they are worth, 
              <strong className="text-magma-red"> bring them in - we will find the value for you, free of charge!</strong>
            </p>

            {/* Highlights grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {highlights.map((h, i) => {
                const Icon = h.icon;
                return (
                  <div
                    key={h.title}
                    data-testid={`about-highlight-${h.title.toLowerCase().replace(/\s+/g, '-')}`}
                    className="reveal-child stone-card bg-bone-white border-3 border-dirt-brown/20 rounded-rock-lg p-5 group"
                  >
                    <div className="w-10 h-10 bg-magma-red/10 rounded-full flex items-center justify-center mb-3 group-hover:bg-magma-red transition-colors duration-300">
                      <Icon size={20} className="text-magma-red group-hover:text-white transition-colors duration-300" />
                    </div>
                    <h3 className="font-heading text-base text-dirt-brown uppercase mb-1">{h.title}</h3>
                    <p className="text-sm text-muted-foreground font-body">{h.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right - Caveman sticker */}
          <div className="lg:col-span-2 flex items-center justify-center">
            <div className="relative">
              <img
                src={CAVEMAN_LAPTOP}
                alt="Cartoon caveman using a laptop - Cave City Pawn goes digital"
                className="w-48 md:w-64 lg:w-80 caveman-float drop-shadow-xl"
                loading="lazy"
              />
              {/* Speech bubble */}
              <div className="absolute -top-4 -left-4 md:-top-8 md:-left-10 bg-bone-white border-3 border-dirt-brown rounded-rock-lg p-3 md:p-4 shadow-soft max-w-[180px]">
                <p className="text-xs md:text-sm font-heading text-dirt-brown uppercase leading-tight">
                  Free Appraisals!
                </p>
                {/* Bubble tail */}
                <div className="absolute -bottom-2 right-6 w-4 h-4 bg-bone-white border-b-3 border-r-3 border-dirt-brown transform rotate-45" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
